import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {actions as accountActions, selectors as accountSelectors} from '../../Ducks/account';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

/**
 * LinkedIn OAuth Callback Handler
 *
 * This component handles the OAuth callback from LinkedIn after user authorization.
 * It extracts the authorization code from the URL and sends it to the backend
 * for token exchange and authentication.
 */
const LinkedInCallback = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const errorMessage = useSelector(state => accountSelectors.errorMessage(state));

    // Handle authentication error
    useEffect(() => {
        if (errorMessage) {
            navigate('/', {state: {error: errorMessage}});
        }
    }, [errorMessage, navigate]);

    // Handle authentication process
    useEffect(() => {
        const handleLinkedInCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const state = urlParams.get('state');
            const userId = urlParams.get('userId');
            const error = urlParams.get('error');

            if (error) {
                navigate('/', {state: {error: 'LinkedIn login failed. Unable to recognize you as Network Director.'}});
                return;
            }

            if (!code) {
                navigate('/', {state: {error: 'LinkedIn login failed. Unable to recognize you as Network Director.'}});
                return;
            }

            // Clean up stored state
            sessionStorage.removeItem('linkedin_oauth_state');

            // Authenticate with backend using the authorization code
            // The backend will handle the token exchange to avoid CORS issues
            const redirectUri = window.location.origin + "/linkedin/callback";
            dispatch(accountActions.linkedinLogin({
                code: code,
                redirect_uri: redirectUri,
                state: state, //to check CSRF attacks at backend
                userId: userId
            }));
            navigate("/");
        };

        handleLinkedInCallback();
    }, [navigate, dispatch]);

    if (errorMessage) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
            >
                <Typography variant="h6" color="error" sx={{mb: 3, textAlign: 'center', p: 2}}>
                    {errorMessage}
                </Typography>
                {/* eslint-disable-next-line react/jsx-no-undef */}
                <Button variant="contained" onClick={() => navigate('/')}>
                    Back to Login
                </Button>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
        >
            <CircularProgress size={60}/>
            <Typography variant="h6" sx={{mt: 3}}>
                Authenticating with LinkedIn...
            </Typography>
        </Box>
    );
};

export default LinkedInCallback;
